import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import {useAuth0} from "@auth0/auth0-react";
import {toast} from "react-toastify";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

import PageHeaderRowSearch from "../components/shared/page-header-row-search";
import {
  FiltersSections,
  FiltersTargets,
  MetricsUtilizationFilters
} from "../components/metrics-utilization/metrics-utilization-filters";
import ai from "../helpers/axios";
import {
  MetricsUtilizationTable
} from "../components/metrics-utilization/metrics-utilization-table";
import {MetricsType} from "../components/metrics-utilization/metrics-utilization-table";

const tableDefaultState = {
  rows: [],
  totalItems: 0,
  countPerPage: 10,
  currentPage: 1,
  sortBy: "totalCost",
  sortDirection: "desc",
};

const initialState = {
  loading: {
    [FiltersTargets.OFR]: {
      [MetricsType.byAircraftType]: false,
      [MetricsType.byAircraftModel]: false,
    },
    [FiltersTargets.Contract]: {
      [MetricsType.byAircraftType]: false,
      [MetricsType.byAircraftModel]: false,
    },
  },
  [FiltersTargets.OFR]: {
    [MetricsType.byAircraftType]: tableDefaultState,
    [MetricsType.byAircraftModel]: tableDefaultState,
    grandTotalDuration: null,
    grandTotalCost: null,
  },
  [FiltersTargets.Contract]: {
    [MetricsType.byAircraftType]: tableDefaultState,
    [MetricsType.byAircraftModel]: tableDefaultState,
    grandTotalDuration: null,
    grandTotalCost: null,
  },
  filters: {},
}

const EntityLabel = {
  [FiltersTargets.OFR]: "OFR",
  [FiltersTargets.Contract]: "Contract",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "loadingChanged": {
      const tableType = action.payload.type;

      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.target]: tableType ? {
            ...state.loading[action.payload.target],
            [tableType]: action.payload.value,
          } : {
            [MetricsType.byAircraftType]: action.payload.value,
            [MetricsType.byAircraftModel]: action.payload.value,
          },
        }
      }
    }

    case "filtersChanged": {
      return {
        ...state,
        filters: action.filters,
      }
    }

    case "newMetricsDownloaded": {
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.target]: false,
        },
        [action.target]: {
          ...state[action.target],
          ...action.data,
          [MetricsType.byAircraftType]: {
            ...state[action.target][MetricsType.byAircraftType],
            rows: action.data[MetricsType.byAircraftType].rows,
            totalItems: action.data[MetricsType.byAircraftType].totalItems,
          },
          [MetricsType.byAircraftModel]: {
            ...state[action.target][MetricsType.byAircraftModel],
            rows: action.data[MetricsType.byAircraftModel].rows,
            totalItems: action.data[MetricsType.byAircraftModel].totalItems,
          }
        }
      }
    }

    case "pageChanged": {
      return {
        ...state,
        [action.payload.target]: {
          ...state[action.payload.target],
          [action.payload.type]: {
            ...state[action.payload.target][action.payload.type],
            currentPage: action.payload.page,
          },
        },
      }
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        [action.payload.target]: {
          ...state[action.payload.target],
          [action.payload.type]: {
            ...state[action.payload.target][action.payload.type],
            countPerPage: action.payload.countPerPage,
            currentPage: 1,
          }
        }
      }
    }

    case "sortingChanged": {
      return {
        ...state,
        [action.payload.target]: {
          ...state[action.payload.target],
          [action.payload.type]: {
            ...state[action.payload.target][action.payload.type],
            ...(state[action.payload.target][action.payload.type].currentPage !== 1 && {
              currentPage: 1,
            }),
            sortBy: action.payload.sortBy,
            sortDirection: action.payload.sortDirection,
          }
        }
      }
    }

    default:
      break;
  }
}

const MetricsUtilization = () => {
    const {
      getAccessTokenSilently,
      loginWithRedirect
    } = useAuth0();

  useEffect(() => {
    document.title = "Metrics - Utilization | evoJets HQ";
  }, []);

    const [state, dispatch] = useReducer(reducer, initialState);

    const formatAirportSectionFilters = (sectionFilters) => {
      const formatedFilters = {};

      for (const field in sectionFilters) {
        formatedFilters[field] = (sectionFilters[field] ?? []).map(({value}) => value);
      }

      return formatedFilters;
    };

    const getUtilizationMetrics = useCallback(async ({target, targetFilters, token, partialLoad}) => {
      if (!targetFilters) return;

      dispatch({
        type: "loadingChanged",
        payload: {
          target,
          value: true,
          ...(partialLoad?.length && {type: partialLoad[0]}),
        },
      });

      const formatedFilters = {
        dates: targetFilters.dates,
        [FiltersSections.Departure]: formatAirportSectionFilters(targetFilters[FiltersSections.Departure]),
        [FiltersSections.Arrival]: formatAirportSectionFilters(targetFilters[FiltersSections.Arrival]),
        [MetricsType.byAircraftType]: {
          currentPage: state[target][MetricsType.byAircraftType].currentPage,
          countPerPage: state[target][MetricsType.byAircraftType].countPerPage,
          sortBy: state[target][MetricsType.byAircraftType].sortBy,
          sortDirection: state[target][MetricsType.byAircraftType].sortDirection,
        },
        [MetricsType.byAircraftModel]:
          {
            currentPage: state[target][MetricsType.byAircraftModel].currentPage,
            countPerPage: state[target][MetricsType.byAircraftModel].countPerPage,
            sortBy: state[target][MetricsType.byAircraftModel].sortBy,
            sortDirection: state[target][MetricsType.byAircraftModel].sortDirection,
          },
      };

      try {
        let response = await ai.auth(token).post(`/api/metrics/utilization/${target}`, formatedFilters);

        if (!response?.data?.success) {
          dispatch({
            type: "loadingChanged",
            payload: {
              target,
              value: false,
              ...(partialLoad?.length && {type: partialLoad[0]}),
            }
          });

          toast.error(`Error getting ${EntityLabel[target]}s utilization metrics`);
        }

        dispatch({
          type: "newMetricsDownloaded",
          target,
          data: response.data.metrics,
        });

        toast.success(`${EntityLabel[target]}s utilization metrics loaded`);
      } catch (e) {
        console.error(e);

        dispatch({
          type: "loadingChanged",
          payload: {
            target,
            value: false,
            ...(partialLoad?.length && {type: partialLoad[0]}),
          }
        });

        toast.error(`Error getting ${EntityLabel[target]}s utilization metrics`);
      }
    }, [state]);

    const fetchData = useCallback(async ({target, filters = state.filters, partialLoad}) => {
      const token = await getAccessTokenSilently().catch(() => {
        return loginWithRedirect();
      });

      const promises = [];

      const targetFilters = filters.areShared ? filters[FiltersTargets.Shared] : filters[target];

      if ([FiltersTargets.Shared, FiltersTargets.OFR].includes(target)) {
        promises.push(getUtilizationMetrics({
          target: FiltersTargets.OFR,
          targetFilters,
          token,
          partialLoad,
        }));
      }

      if ([FiltersTargets.Shared, FiltersTargets.Contract].includes(target)) {
        promises.push(getUtilizationMetrics({
            target: FiltersTargets.Contract,
            targetFilters,
            token,
            partialLoad,
          }
        ));
      }

      await Promise.allSettled(promises)
    }, [getAccessTokenSilently, getUtilizationMetrics, loginWithRedirect, state.filters]);

    const applyFilters = async ({target, filters}) => {
      dispatch({
        type: "filtersChanged",
        filters,
        target,
      });

      fetchData({target, filters});
    }

    return (
      <Fragment>
        <PageHeaderRowSearch h1="Aircraft Utilization"/>

        <MetricsUtilizationFilters applyFilters={applyFilters}/>

        <Stack gap={5} mt={5}>
          {[MetricsType.byAircraftType, MetricsType.byAircraftModel].map(type =>
            <Stack direction="row" gap={5} flexWrap="wrap">
              {[FiltersTargets.OFR, FiltersTargets.Contract].map(target =>
                <Box flex={1}>
                  <MetricsUtilizationTable
                    target={target}
                    type={type}
                    rows={state[target][type].rows}
                    grandTotalDuration={state[target].grandTotalDuration}
                    grandTotalCost={state[target].grandTotalCost}
                    isLoading={state.loading[target][type]}
                    parentDispatch={dispatch}
                    fetchData={fetchData}
                    pagination={{
                      countPerPage: state[target][type].countPerPage,
                      currentPage: state[target][type].currentPage,
                      totalItems: state[target][type].totalItems,
                    }}
                    sorting={{
                      sortBy: state[target][type].sortBy,
                      sortDirection: state[target][type].sortDirection,
                    }}
                  />
                </Box>
              )}
            </Stack>
          )}
        </Stack>

      </Fragment>
    );
  }
;

export default MetricsUtilization;
